import { ArrowRight, Shield, CheckCircle, TrendingUp, Award, PieChart, Star, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import OptimizedImage from './OptimizedImage';
import SEO from './SEO';
import { pageSEO, faqSchemas } from '../seoData';
import RelatedServices from './RelatedServices';

const MutualFundAdvisor = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO {...pageSEO.mutualFundAdvisor} faqJsonLd={faqSchemas.mutualFundAdvisor} />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-purple-50 pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="mb-6">
                <span className="inline-block bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold tracking-wide">
                  EXPERT MUTUAL FUND ADVISORY
                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Best Mutual Fund Advisor
                <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  In India Since 1996
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Expert mutual fund advisory for wealth creation through systematic investing. 
                As SEBI-registered advisors, we help you build diversified portfolios across equity, 
                debt, and hybrid funds from top-rated AMCs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 inline-flex items-center justify-center shadow-xl"
                >
                  Start SIP Today
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/calculator"
                  className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-4 rounded-xl font-bold text-lg transition-all inline-flex items-center justify-center"
                >
                  SIP Calculator
                </Link>
              </div>
            </div>
            
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <OptimizedImage 
                  src="https://images.pexels.com/photos/6801647/pexels-photo-6801647.jpeg?w=800&q=85" 
                  alt="Best mutual fund advisor India - Expert SIP investment planning and portfolio management for wealth creation through systematic investing since 1996"
                  className="w-full h-[500px]"
                  priority={true}
                  objectFit="cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/10 to-purple-600/10"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                25+ Years
              </div>
              <div className="text-gray-600">Mutual Fund Advisory</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                ₹200 Cr+
              </div>
              <div className="text-gray-600">MF AUM</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                15%+
              </div>
              <div className="text-gray-600">Average Annual Returns</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                5000+
              </div>
              <div className="text-gray-600">Active SIPs</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Why Choose Us as Your Mutual Fund Advisor?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional guidance for smarter mutual fund investments
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">SEBI Registered Advisor</h3>
              <p className="text-gray-600 leading-relaxed">
                Fully regulated by SEBI with complete transparency. We recommend only those funds that 
                align with your goals - no commission bias, pure advisory focused on your wealth growth.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6">
                <PieChart className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Diversified Portfolio Building</h3>
              <p className="text-gray-600 leading-relaxed">
                Expert asset allocation across equity (large, mid, small cap), debt, and hybrid funds. 
                We create balanced portfolios that match your risk appetite and financial goals.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6">
                <Target className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Goal-Based Planning</h3>
              <p className="text-gray-600 leading-relaxed">
                Whether it's child education, retirement, home purchase, or wealth creation - we design 
                SIP strategies tailored to achieve your specific financial goals on time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Our Mutual Fund Advisory Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive mutual fund solutions for all investment needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Systematic Investment Plans (SIP)",
                description: "Start investing with as low as ₹500/month. Build wealth systematically through rupee cost averaging",
                minInvestment: "₹500/month"
              },
              {
                title: "Lumpsum Investment Advisory",
                description: "Strategic lumpsum deployment based on market conditions and fund performance analysis",
                minInvestment: "₹25,000"
              },
              {
                title: "Equity Mutual Funds",
                description: "Large cap, mid cap, small cap, and flexi cap funds for long-term wealth creation",
                minInvestment: "₹1,000/month"
              },
              {
                title: "Debt Mutual Funds",
                description: "Liquid, short duration, and corporate bond funds for stable returns and capital preservation",
                minInvestment: "₹5,000"
              },
              {
                title: "Hybrid Funds",
                description: "Balanced advantage and aggressive hybrid funds for optimal risk-reward balance",
                minInvestment: "₹1,000/month"
              },
              {
                title: "ELSS Tax Saving Funds",
                description: "Equity Linked Savings Schemes for 80C tax benefits with wealth creation potential",
                minInvestment: "₹500/month"
              }
            ].map((service, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 hover:shadow-xl transition-all">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-700 mb-4 leading-relaxed">{service.description}</p>
                <div className="text-sm font-semibold text-blue-600">Min Investment: {service.minInvestment}</div>
              </div>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <Link
              to="/services"
              className="inline-flex items-center bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-xl"
            >
              Explore All Services
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* SIP Benefits */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Power of Systematic Investing
            </h2>
            <p className="text-xl text-gray-600">
              Why SIP is the smartest way to invest in mutual funds
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Disciplined Investing",
                description: "Automate your investments and build wealth consistently without emotional decisions"
              },
              {
                step: "02",
                title: "Rupee Cost Averaging",
                description: "Buy more units when markets are low, less when high - averaging your purchase cost"
              },
              {
                step: "03",
                title: "Power of Compounding",
                description: "Reinvested returns generate their own returns, exponentially growing your wealth"
              },
              {
                step: "04",
                title: "Goal Achievement",
                description: "Systematic approach ensures you stay on track to achieve your financial goals"
              }
            ].map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <span className="text-2xl font-bold text-white">{step.step}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              SIP Success Stories
            </h2>
            <p className="text-xl text-gray-600">
              Real clients, real returns, real wealth creation
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Priya Mehta",
                location: "Mumbai",
                testimonial: "Started ₹10,000 monthly SIP 10 years ago on their advice. Today my portfolio is worth ₹32 lakhs! Best mutual fund advisor I've worked with. Their fund selection is excellent."
              },
              {
                name: "Rahul Sharma",
                location: "Delhi",
                testimonial: "Was confused about which mutual funds to choose. Rumtek created a perfect diversified portfolio for me. 12 years of SIP has helped me accumulate ₹1.8 crores for retirement!"
              },
              {
                name: "Anita Desai",
                location: "Bangalore",
                testimonial: "Their goal-based SIP planning helped me save for my daughter's education. Started with ₹5000/month, now have ₹18 lakhs after 8 years. Professional and trustworthy advisors!"
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 shadow-lg">
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic leading-relaxed">"{testimonial.testimonial}"</p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    {testimonial.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.location}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Mutual Fund Investment FAQs
            </h2>
            <p className="text-xl text-gray-600">
              Common questions about mutual fund investing
            </p>
          </div>
          
          <div className="space-y-6">
            {[
              {
                question: "What is the minimum amount to start SIP?",
                answer: "You can start a SIP with as little as ₹500 per month in most mutual funds. We recommend starting with at least ₹1,000-5,000 monthly based on your income and goals. The key is to start early and stay consistent."
              },
              {
                question: "How do I choose the right mutual funds?",
                answer: "Fund selection depends on your goals, risk appetite, and time horizon. As professional advisors, we analyze fund performance, expense ratios, fund manager track record, and portfolio composition to recommend the best funds for you."
              },
              {
                question: "Can I withdraw my mutual fund investment anytime?",
                answer: "Yes, open-ended mutual funds offer liquidity. You can redeem anytime (except ELSS which has 3-year lock-in). However, for optimal returns, we recommend staying invested as per your goal timeline."
              },
              {
                question: "Are mutual funds safe investments?",
                answer: "Mutual funds are regulated by SEBI and professionally managed. While they carry market risk, diversified portfolios spread risk effectively. Our advisory helps you choose funds matching your risk profile for safe, steady wealth creation."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <RelatedServices links={['equity', 'pms', 'bonds', 'gold', 'fd', 'calculator']} />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-purple-900 to-blue-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Start Your SIP Journey Today!
          </h2>
          <p className="text-xl text-blue-100 mb-10 leading-relaxed">
            Build wealth systematically with expert mutual fund advisory. 
            Schedule a free consultation and discover the power of disciplined investing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-blue-900 px-10 py-5 rounded-xl font-bold text-lg hover:bg-blue-50 transition-all transform hover:scale-105 inline-flex items-center justify-center shadow-2xl"
            >
              Start Your SIP Now
              <ArrowRight className="ml-2 h-6 w-6" />
            </Link>
            <Link
              to="/calculator"
              className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-10 py-5 rounded-xl font-bold text-lg transition-all inline-flex items-center justify-center"
            >
              Calculate SIP Returns
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MutualFundAdvisor;